// ============================================
// ASSISTANT IA — LIBELLÉS ET RÈGLES D'AFFICHAGE
// ============================================
// Fonctions pures, testées : ce que l'écran dit de l'assistant (messages
// automatiques, pause de l'assistant client) et comment une action proposée
// devient une mise à jour du dossier.

import { STATUS_LABELS, PRIORITY_LABELS, ISSUE_TYPE_LABELS } from '../../shared/labels';
import type { IssueType, Ticket, TicketMessage, TicketPriority, TicketStatus, UpdateTicketPayload } from '../../types';
import type { ActionProposee, ChampEquipementDossier, EtatClient } from './api';

/**
 * Le message vient-il de l'assistant automatique (et non d'un conseiller) ?
 * Seul le champ posé par le serveur (estAssistant, d'après l'adresse du compte
 * technique) ou par l'événement temps réel (isAI) fait foi : un nom affiché
 * peut être choisi par n'importe quel compte.
 */
export function estMessageAssistant(m: Pick<TicketMessage, 'estAssistant'>): boolean {
  return m.estAssistant === true;
}

const RAISONS_PAUSE: Array<[RegExp, string]> = [
  [/conseiller a (déjà )?répondu/i, 'Un conseiller a répondu au client : l\'assistant automatique ne lui écrit plus dans ce dossier.'],
  [/client a demandé une personne/i, 'Le client a demandé à parler à une personne : l\'assistant automatique s\'est retiré.'],
  [/passé la main/i, 'L\'assistant automatique a passé la main à un conseiller.'],
  [/fusionn/i, 'Dossier fusionné dans un autre : l\'assistant automatique n\'y écrit plus.'],
  [/e-?mail/i, 'Dossier arrivé par e-mail : l\'assistant automatique n\'y répond pas.'],
  [/ouvert par un conseiller/i, 'Dossier ouvert par un conseiller : l\'assistant automatique n\'y répond pas.'],
  [/téléphone/i, 'Dossier ouvert au téléphone : l\'assistant automatique n\'y répond pas.'],
];

/** Pourquoi l'assistant client s'est tu, en une phrase ; null s'il n'est pas en pause. */
export function libellePauseAssistant(ticket: Pick<Ticket, 'aiMuted' | 'aiMutedReason'> | null | undefined): string | null {
  if (!ticket?.aiMuted) return null;
  const raison = (ticket.aiMutedReason ?? '').trim();
  for (const [motif, phrase] of RAISONS_PAUSE) if (motif.test(raison)) return phrase;
  return raison
    ? `L'assistant automatique est en pause sur ce dossier (${raison}).`
    : 'L\'assistant automatique est en pause sur ce dossier.';
}

export const LIBELLES_ETAT_CLIENT: Record<EtatClient, string> = {
  satisfait: 'Satisfait',
  neutre: 'Neutre',
  preoccupe: 'Préoccupé',
  frustre: 'Frustré',
  urgent: 'Urgent',
};

export const COULEURS_ETAT_CLIENT: Record<EtatClient, string> = {
  satisfait: 'bg-green-100 text-green-800',
  neutre: 'bg-brume text-encre',
  preoccupe: 'bg-amber-100 text-amber-800',
  frustre: 'bg-orange-100 text-orange-800',
  urgent: 'bg-red-100 text-red-800',
};

export const LIBELLES_CHAMP: Record<ChampEquipementDossier, string> = {
  equipmentBrand: 'Marque',
  equipmentModel: 'Modèle',
  serialNumber: 'Numéro de série',
  errorCode: 'Code erreur',
};

export const LIBELLES_CONFIANCE: Record<'faible' | 'moyenne' | 'elevee', string> = {
  faible: 'confiance faible',
  moyenne: 'confiance moyenne',
  elevee: 'confiance élevée',
};

/** Ce que fait l'action, en clair (« Passer en : En attente du client »). */
export function decrireAction(a: ActionProposee): string {
  switch (a.type) {
    case 'statut': return `Passer en « ${STATUS_LABELS[a.valeur as TicketStatus] ?? a.valeur} »`;
    case 'priorite': return `Priorité « ${PRIORITY_LABELS[a.valeur as TicketPriority] ?? a.valeur} »`;
    case 'type': return `Type « ${ISSUE_TYPE_LABELS[a.valeur as IssueType] ?? a.valeur} »`;
    case 'equipement': return `${a.champ ? LIBELLES_CHAMP[a.champ] : 'Équipement'} : ${a.valeur}`;
    case 'reponse_type': return `Réponse type « ${a.reponseType?.titre ?? a.valeur} »`;
    default: return a.valeur;
  }
}

/**
 * Mise à jour du dossier correspondant à l'action, ou null si l'action ne
 * modifie pas le dossier (réponse type : elle va dans le brouillon) ou si
 * elle ne s'applique plus (valeur déjà en place, champ rempli entre-temps).
 */
export function miseAJourPourAction(a: ActionProposee, ticket: Partial<Ticket>): UpdateTicketPayload | null {
  switch (a.type) {
    case 'statut': return ticket.status === a.valeur ? null : { status: a.valeur as TicketStatus };
    case 'priorite': return ticket.priority === a.valeur ? null : { priority: a.valeur as TicketPriority };
    case 'type': return ticket.issueType === a.valeur ? null : { issueType: a.valeur as IssueType };
    case 'equipement':
      if (!a.champ || (ticket[a.champ] ?? '').toString().trim()) return null;
      return { [a.champ]: a.valeur } as UpdateTicketPayload;
    default: return null;
  }
}
